"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import type mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import routesData from "./routes-data.json";
import { cn } from "@/lib/utils";

type Point = [number, number, number]; // lon, lat, ele(m)
type LngLat = [number, number];

interface RouteFeature {
  id: string;
  name: string;
  km: number;
  points: Point[];
}

const DATA = routesData as unknown as { start: Point; features: RouteFeature[] };
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
const MAP_STYLE = "mapbox://styles/mapbox/standard";

/** Fixed flight duration for a full out-and-back lap, independent of real distance/pace. */
const LAP_SECONDS = 55;
const FINISH_PAUSE_SECONDS = 3;

/** Official GPX files, proxied same-origin by the /recorridos rewrite so `download` saves them. */
const gpxFile = (id: string) => `${id.toUpperCase()}-MES-2026.gpx`;

const R = 6371000;
const toRad = (d: number) => (d * Math.PI) / 180;
const haversine = (a: LngLat, b: LngLat): number => {
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
const bearing = (a: LngLat, b: LngLat): number => {
  const y = Math.sin(toRad(b[0] - a[0])) * Math.cos(toRad(b[1]));
  const x =
    Math.cos(toRad(a[1])) * Math.sin(toRad(b[1])) -
    Math.sin(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.cos(toRad(b[0] - a[0]));
  return (Math.atan2(y, x) * 180) / Math.PI;
};

interface BuiltRoute {
  id: string;
  name: string;
  km: number;
  coords: LngLat[];
  eles: number[];
  frac: number[]; // cumulative distance fraction per vertex, 0..1
  length: number; // meters
  bounds: [LngLat, LngLat];
  minEle: number;
  maxEle: number;
  turnIdx: number;
}

function build(f: RouteFeature): BuiltRoute {
  const coords = f.points.map((p) => [p[0], p[1]] as LngLat);
  const eles = f.points.map((p) => p[2]);
  let total = 0;
  const cum = [0];
  for (let i = 1; i < coords.length; i++) {
    total += haversine(coords[i - 1], coords[i]);
    cum.push(total);
  }
  const frac = cum.map((c) => c / (total || 1));
  const lons = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  // Out-and-back course: the turnaround is the point closest to the midpoint of the
  // total path *distance*, not the point farthest in a straight line from the start
  // (which can miss the real turnaround on a curved coastline).
  let turnIdx = 0;
  let bestDiff = Infinity;
  const half = total / 2;
  for (let i = 0; i < cum.length; i++) {
    const diff = Math.abs(cum[i] - half);
    if (diff < bestDiff) {
      bestDiff = diff;
      turnIdx = i;
    }
  }
  return {
    id: f.id,
    name: f.name,
    km: f.km,
    coords,
    eles,
    frac,
    length: total,
    bounds: [
      [Math.min(...lons), Math.min(...lats)],
      [Math.max(...lons), Math.max(...lats)],
    ],
    minEle: Math.min(...eles),
    maxEle: Math.max(...eles),
    turnIdx,
  };
}

/** Point + elevation at `frac` (0..1) along the route, plus the index of the segment start. */
function along(r: BuiltRoute, frac: number) {
  const f = Math.min(Math.max(frac, 0), 1);
  let lo = 0;
  let hi = r.frac.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (r.frac[mid] <= f) lo = mid;
    else hi = mid;
  }
  const span = r.frac[hi] - r.frac[lo] || 1;
  const t = (f - r.frac[lo]) / span;
  const a = r.coords[lo];
  const b = r.coords[hi];
  const point: LngLat = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  const ele = r.eles[lo] + (r.eles[hi] - r.eles[lo]) * t;
  return { point, ele, index: lo };
}

/**
 * Whole-course view. The courses run east–west along the coast, so on a portrait map (phones)
 * the camera turns to face along the course and it fills the height instead of a thin strip.
 */
function fitOverview(map: mapboxgl.Map, r: BuiltRoute) {
  const { clientWidth: w, clientHeight: h } = map.getContainer();
  const portrait = h > w * 1.1;
  map.fitBounds(r.bounds, {
    // Top padding keeps the course clear of the km/elevation HUD.
    padding: portrait ? { top: 72, bottom: 64, left: 28, right: 28 } : { top: 60, bottom: 60, left: 40, right: 40 },
    pitch: portrait ? 35 : 60,
    ...(portrait && { bearing: bearing(r.coords[0], r.coords[r.turnIdx]) }),
    duration: 900,
  });
}

function markerEl(className: string, inner: string) {
  const el = document.createElement("div");
  el.className = className;
  el.innerHTML = inner;
  return el;
}

const RouteMapSection: React.FC = () => {
  const routes = useMemo(() => DATA.features.map(build), []);
  const [active, setActive] = useState(routes[routes.length - 1].id);
  const activeRoute = routes.find((r) => r.id === active) ?? routes[0];

  const [mapReady, setMapReady] = useState(false);
  const [mapFailed, setMapFailed] = useState(!MAPBOX_TOKEN);
  const [playing, setPlaying] = useState(false);
  const [followMode, setFollowMode] = useState(true);
  const [inView, setInView] = useState(true);
  const [hud, setHud] = useState({ km: 0, ele: 0 });

  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapboxglRef = useRef<typeof mapboxgl | null>(null);
  const runnerRef = useRef<mapboxgl.Marker | null>(null);
  const staticMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const chartPathRef = useRef<SVGPathElement>(null);
  const chartDotRef = useRef<SVGCircleElement>(null);
  const scrubRef = useRef<HTMLInputElement>(null);

  const progressRef = useRef(0); // 0..1 along the route
  const playingRef = useRef(false);
  const followRef = useRef(true);
  const smoothedBearingRef = useRef(0);
  const setFrameRef = useRef<(frac: number, userDriven?: boolean) => void>(() => {});

  useEffect(() => {
    playingRef.current = playing && inView;
  }, [playing, inView]);
  useEffect(() => {
    followRef.current = followMode;
  }, [followMode]);

  /* Pause automatically while the section is off-screen. */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.05,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /* ---------- Mapbox setup (once) ---------- */
  useEffect(() => {
    if (mapFailed || !containerRef.current || mapRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const mb = (await import("mapbox-gl")).default;
        if (cancelled || !containerRef.current) return;
        mapboxglRef.current = mb;
        mb.accessToken = MAPBOX_TOKEN as string;
        const mid = activeRoute.coords[activeRoute.turnIdx];
        const map = new mb.Map({
          container: containerRef.current,
          style: MAP_STYLE,
          center: mid,
          zoom: 12.5,
          pitch: 60,
          bearing: 20,
          antialias: true,
          preserveDrawingBuffer: true,
          cooperativeGestures: true,
          attributionControl: true,
        });
        mapRef.current = map;
        map.addControl(new mb.NavigationControl({ visualizePitch: true }), "bottom-right");

        map.once("error", () => setMapFailed(true));

        map.on("style.load", () => {
          try {
            map.setConfigProperty("basemap", "lightPreset", "night");
            map.setConfigProperty("basemap", "showPointOfInterestLabels", false);
            map.setConfigProperty("basemap", "show3dObjects", true);
          } catch {
            /* older style schema without config properties */
          }
          if (!map.getSource("mapbox-dem")) {
            map.addSource("mapbox-dem", {
              type: "raster-dem",
              url: "mapbox://mapbox.mapbox-terrain-dem-v1",
              tileSize: 512,
              maxzoom: 14,
            });
            map.setTerrain({ source: "mapbox-dem", exaggeration: 1.3 });
          }
          setMapReady(true);
        });
      } catch {
        setMapFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapFailed]);

  /* ---------- (Re)build sources, layers and static markers for the active route ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const routeSourceData: GeoJSON.Feature<GeoJSON.LineString> = {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: activeRoute.coords },
    };
    const emptyLine: GeoJSON.Feature<GeoJSON.LineString> = {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: [activeRoute.coords[0], activeRoute.coords[0]] },
    };

    const lineWidthByZoom = (a: number, b: number, c: number): mapboxgl.Expression => [
      "interpolate",
      ["linear"],
      ["zoom"],
      10,
      a,
      13.5,
      b,
      16.5,
      c,
    ];

    if (!map.getSource("route")) {
      map.addSource("route", { type: "geojson", data: routeSourceData });
      map.addSource("trail", { type: "geojson", data: emptyLine, lineMetrics: true });
      map.addLayer({
        id: "route-glow",
        type: "line",
        slot: "top",
        source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#0032FF",
          "line-width": lineWidthByZoom(14, 22, 34),
          "line-blur": 16,
          "line-opacity": 0.4,
          "line-emissive-strength": 1,
        },
      });
      map.addLayer({
        id: "route-line",
        type: "line",
        slot: "top",
        source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#ffffff",
          "line-width": lineWidthByZoom(4.5, 7, 11),
          "line-opacity": 0.8,
          "line-emissive-strength": 1,
        },
      });
      map.addLayer({
        id: "trail-glow",
        type: "line",
        slot: "top",
        source: "trail",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#ffd400",
          "line-width": lineWidthByZoom(16, 26, 40),
          "line-blur": 18,
          "line-opacity": 0.55,
          "line-emissive-strength": 1,
        },
      });
      map.addLayer({
        id: "trail-line",
        type: "line",
        slot: "top",
        source: "trail",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#ffd400",
          "line-width": lineWidthByZoom(6, 9, 14),
          "line-emissive-strength": 1,
        },
      });
    } else {
      (map.getSource("route") as mapboxgl.GeoJSONSource).setData(routeSourceData);
      (map.getSource("trail") as mapboxgl.GeoJSONSource).setData(emptyLine);
    }

    staticMarkersRef.current.forEach((m) => m.remove());
    staticMarkersRef.current = [];
    const mb = mapboxglRef.current;
    if (mb) {
      const startEl = markerEl("mes-pin mes-pin--white", "");
      staticMarkersRef.current.push(new mb.Marker({ element: startEl }).setLngLat(activeRoute.coords[0]).addTo(map));

      const turnEl = markerEl("mes-pin mes-pin--amber", "");
      staticMarkersRef.current.push(
        new mb.Marker({ element: turnEl }).setLngLat(activeRoute.coords[activeRoute.turnIdx]).addTo(map)
      );

      const runnerEl = markerEl("mes-runner", '<span class="mes-runner-pulse"></span><span class="mes-runner-dot"></span>');
      runnerEl.style.setProperty("--runner-color", "#ffd400");
      runnerRef.current = new mb.Marker({ element: runnerEl, anchor: "center" }).setLngLat(activeRoute.coords[0]).addTo(map);
    }

    fitOverview(map, activeRoute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, mapReady]);

  /* ---------- Reset playback whenever the distance changes ---------- */
  useEffect(() => {
    progressRef.current = 0;
    setPlaying(false);
    setHud({ km: 0, ele: activeRoute.eles[0] ?? 0 });
    if (scrubRef.current) scrubRef.current.value = "0";
    if (chartDotRef.current) chartDotRef.current.setAttribute("cx", "0");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  /* ---------- Elevation profile path (redraws only when the active route changes) ---------- */
  useEffect(() => {
    const path = chartPathRef.current;
    if (!path) return;
    const { eles, minEle, maxEle } = activeRoute;
    const span = maxEle - minEle || 1;
    const n = eles.length;
    const d = eles
      .map((e, i) => {
        const x = (i / (n - 1)) * 1000;
        const y = 100 - ((e - minEle) / span) * 90;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
    path.setAttribute("d", d);
  }, [activeRoute]);

  /* ---------- Render loop: advances progress, moves the runner, redraws the trail, drives the camera ---------- */
  useEffect(() => {
    let raf = 0;
    let last = performance.now();

    const setFrame = (frac: number, userDriven = false) => {
      progressRef.current = frac;
      const r = activeRoute;
      const { point, ele, index } = along(r, frac);
      const km = frac * (r.length / 1000);
      setHud({ km, ele });
      if (scrubRef.current) scrubRef.current.value = String(Math.round(frac * 1000));
      if (chartDotRef.current) chartDotRef.current.setAttribute("cx", (frac * 1000).toFixed(1));

      runnerRef.current?.setLngLat(point);
      const map = mapRef.current;
      const src = map?.getSource("trail") as mapboxgl.GeoJSONSource | undefined;
      src?.setData({
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: [...r.coords.slice(0, index + 1), point] },
      });

      if (map && (followRef.current || userDriven)) {
        const ahead = along(r, Math.min(frac + 0.004, 1)).point;
        const rawBearing = bearing(point, ahead);
        const prev = smoothedBearingRef.current;
        let delta = rawBearing - prev;
        while (delta > 180) delta -= 360;
        while (delta < -180) delta += 360;
        smoothedBearingRef.current = prev + delta * 0.15;
        if (followRef.current) {
          map.jumpTo({ center: point, bearing: smoothedBearingRef.current, pitch: 65, zoom: 16.2 });
        }
      }
    };

    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      if (playingRef.current) {
        const cycle = LAP_SECONDS + FINISH_PAUSE_SECONDS;
        const t = (progressRef.current * LAP_SECONDS + dt) % cycle;
        const frac = Math.min(t / LAP_SECONDS, 1);
        setFrame(frac);
      }
      raf = requestAnimationFrame(tick);
    };
    setFrameRef.current = setFrame;
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoute]);

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlaying(false);
    const frac = Number(e.target.value) / 1000;
    setFrameRef.current(frac, true);
  };

  const useMap = !mapFailed;

  return (
    <section
      ref={sectionRef}
      className="bg-brand-cream text-brand-ink px-4 md:px-8 pt-12 md:pt-16 pb-14 md:pb-20"
    >
      <h2 className="mes-display text-center text-[clamp(1.9rem,9vw,6.5rem)] leading-[0.9]">
        Recorridos
        <br />
        Oficiales
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 mt-10 md:mt-14">
        {/* Route selector */}
        <div className="lg:col-span-3 grid grid-cols-3 lg:flex lg:flex-col gap-2 md:gap-3">
          <p className="col-span-3 mes-label opacity-70 mb-1">Elige tu distancia</p>
          {routes.map((r) => {
            const isActive = r.id === active;
            return (
              <button
                key={r.id}
                onClick={() => setActive(r.id)}
                aria-pressed={isActive}
                className={cn(
                  "text-left border border-brand-ink px-3 lg:px-4 py-3 transition-colors mes-label",
                  isActive ? "bg-brand-ink text-white" : "hover:bg-brand-ink/5"
                )}
              >
                {/* Phones and tablets: three compact tiles in a row. */}
                <div className="lg:hidden">
                  <span className="mes-display text-xl sm:text-2xl leading-none normal-case">{Math.round(r.km)}KM</span>
                </div>
                <div className="hidden lg:flex items-start justify-between gap-3">
                  <span className="min-w-0">{r.name}</span>
                  <span className={cn("opacity-70 whitespace-nowrap shrink-0", isActive && "opacity-90")}>
                    {r.km.toFixed(1)} km
                  </span>
                </div>
              </button>
            );
          })}
          <a
            href={`/recorridos/${gpxFile(activeRoute.id)}`}
            download={gpxFile(activeRoute.id)}
            className="col-span-3 flex items-center gap-2.5 py-2 lg:mt-1 mes-label hover:underline underline-offset-4"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
            </svg>
            Descargar GPX {Math.round(activeRoute.km)}KM
          </a>
        </div>

        {/* Mapbox 3D route explorer */}
        {/* Below lg the map gets its own height; on desktop the min-height keeps the control bar from squeezing it. */}
        <div className="lg:col-span-9 relative border border-brand-ink bg-brand-navy lg:w-full lg:aspect-[16/9] lg:min-h-[560px] overflow-hidden flex flex-col">
          <div className="relative h-[clamp(340px,62svh,460px)] lg:h-auto lg:flex-1 lg:min-h-0">
            {useMap ? (
              <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 mes-label text-center px-6">
                No se pudo cargar el mapa 3D.
                <br />
                Falta configurar NEXT_PUBLIC_MAPBOX_TOKEN.
              </div>
            )}
            {useMap && !mapReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-brand-navy text-white/60 mes-label z-10">
                Cargando recorrido 3D…
              </div>
            )}

            {/* HUD: km + elevation */}
            <div className="absolute top-3 left-3 md:top-3.5 md:left-4 z-10 flex items-baseline gap-2 md:gap-2.5 bg-brand-navy/80 px-3 md:px-3.5 py-2">
              <span className="mes-display text-2xl md:text-4xl leading-none text-white tabular-nums">
                {hud.km.toFixed(1)}
              </span>
              <span className="mes-label text-white/50">km</span>
              <span className="mes-label text-brand-yellow ml-1">{Math.round(hud.ele)} m</span>
            </div>

            {/* Follow + fit controls */}
            <div className="absolute top-3 right-3 md:top-3.5 md:right-4 z-10 flex gap-2">
              <button
                onClick={() => setFollowMode((f) => !f)}
                className={cn(
                  "h-[34px] px-3 md:px-3.5 mes-label border transition-colors backdrop-blur",
                  followMode
                    ? "bg-brand-yellow/30 border-brand-yellow text-white"
                    : "bg-brand-navy/80 border-white/40 text-white"
                )}
              >
                <span className="md:hidden">Seguir</span>
                <span className="hidden md:inline">Seguir corredor</span>
              </button>
              <button
                aria-label="Vista general"
                onClick={() => {
                  setFollowMode(false);
                  if (mapRef.current) fitOverview(mapRef.current, activeRoute);
                }}
                className="w-[34px] h-[34px] mes-label border border-white/40 bg-brand-navy/80 text-white backdrop-blur"
              >
                ⤢
              </button>
            </div>
          </div>

          {/* Bottom control bar */}
          <div className="shrink-0 bg-brand-navy border-t-2 border-white/25 px-4 md:px-4.5 pt-3.5 pb-3">
            <div className="flex items-center gap-4">
              <button
                aria-label="Reproducir recorrido"
                onClick={() => setPlaying((p) => !p)}
                className="w-[46px] h-[46px] shrink-0 rounded-full bg-brand-yellow text-brand-navy shadow-[0_6px_22px_rgba(255,212,0,0.35)]"
              >
                {playing ? "❚❚" : "▶"}
              </button>
              <div className="flex-1 min-w-0">
                <input
                  ref={scrubRef}
                  type="range"
                  min={0}
                  max={1000}
                  defaultValue={0}
                  onChange={handleScrub}
                  aria-label="Avanzar en el recorrido"
                  className="w-full accent-brand-yellow cursor-pointer"
                />
                <svg viewBox="0 0 1000 110" preserveAspectRatio="none" className="w-full h-[44px] md:h-[60px] block mt-0.5">
                  <path ref={chartPathRef} fill="none" stroke="#ffd400" strokeWidth={2} />
                  <circle ref={chartDotRef} cy={100} r={4} fill="#ffffff" stroke="#0a0a0a" strokeWidth={1.5} />
                </svg>
              </div>
              <div className="shrink-0 text-right">
                <div className="mes-display text-xl md:text-2xl leading-none text-white">
                  {Math.round(activeRoute.maxEle)} m
                </div>
                <div className="mes-label text-white/45 mt-1">altura máx.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 items-center mt-2.5 mes-label text-white/60">
              {[
                ["#ffffff", "Salida / Meta"],
                ["#ffd400", "Retorno"],
              ].map(([c, t]) => (
                <span key={t} className="flex items-center gap-1.5">
                  <i className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: c }} />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RouteMapSection;
